import { useState, useEffect, useRef } from 'react';
import { economicCalendar } from '../api/client';

/**
 * Fetch economic events from Investing.com (via backend proxy)
 * for the visible candle range, filtered by instrument currencies.
 */

const INDEX_CURRENCIES = {
  NAS100: 'USD', USTEC: 'USD', US100: 'USD', NDX: 'USD',
  SPX500: 'USD', US500: 'USD', SP500: 'USD',
  US30: 'USD', DJ30: 'USD',
  DAX: 'EUR', GER40: 'EUR', DE40: 'EUR',
  FTSE: 'GBP', UK100: 'GBP',
  JP225: 'JPY', NIK225: 'JPY',
  XAUUSD: 'USD', GOLD: 'USD',
  XAGUSD: 'USD', SILVER: 'USD',
  USOIL: 'USD', WTIUSD: 'USD', BRENT: 'USD',
  BTCUSD: 'USD', ETHUSD: 'USD',
};

function getCurrencies(instrument) {
  const sym = (instrument || '').toUpperCase();
  if (INDEX_CURRENCIES[sym]) return INDEX_CURRENCIES[sym];
  if (sym.length >= 6) return `${sym.slice(0, 3)},${sym.slice(3, 6)}`;
  return '';
}

function getWeekKey(candles) {
  if (!candles || candles.length === 0) return null;
  const lastTs = candles[candles.length - 1].time || candles[candles.length - 1].timestamp;
  const d = new Date(lastTs * 1000);
  // Get Monday of this week
  const day = d.getUTCDay();
  const diff = d.getUTCDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d);
  monday.setUTCDate(diff);
  const from = monday.toISOString().split('T')[0];
  const friday = new Date(monday);
  friday.setUTCDate(monday.getUTCDate() + 4);
  const to = friday.toISOString().split('T')[0];
  return { from, to };
}

export default function useEconomicNews(instrument, candles, enabled = false) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const cacheRef = useRef({});  // { "from|to|currencies" → events[] }
  const lastWeekRef = useRef(null);

  useEffect(() => {
    if (!enabled || !candles || candles.length === 0) {
      setEvents([]);
      return;
    }

    const week = getWeekKey(candles);
    if (!week) return;

    const currencies = getCurrencies(instrument);
    const cacheKey = `${week.from}|${week.to}|${currencies}`;

    // Don't refetch same week
    if (lastWeekRef.current === cacheKey) return;

    // Check local cache
    if (cacheRef.current[cacheKey]) {
      setEvents(cacheRef.current[cacheKey]);
      lastWeekRef.current = cacheKey;
      return;
    }

    let cancelled = false;
    setLoading(true);

    economicCalendar.get(week.from, week.to, currencies)
      .then((res) => {
        if (cancelled) return;
        const mapped = (res.data.events || []).map((e) => ({
          time: Math.floor(new Date(e.datetime).getTime() / 1000),
          impact: e.impact,
          event: e.event,
          currency: e.currency,
          actual: e.actual,
          forecast: e.forecast,
          previous: e.previous,
        }));
        cacheRef.current[cacheKey] = mapped;
        lastWeekRef.current = cacheKey;
        setEvents(mapped);
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [enabled, instrument, candles]);

  return { events, loading };
}
